'use strict';

import domReady from '@wordpress/dom-ready';
import MicroModal from 'micromodal';
import {raiseForStatus} from './fetch';
import {bootstrapData} from './dom';

const modals = {
  settings: {
    disableScroll: true,
    onClose: (e) => closeModal(e),
  },
  path: window.location.pathname,
  classes: {
    modal: 'js-modal',
    trigger: 'js-modal-trigger',
    innerContent: 'js-modal-content',
    init: 'init-modals',
    active: 'has-active-modal',
  },
};

// handling refreshing when viewing a remote modal state
// we go back to the original page because back button won't reload
if (window.history.state) {
  if (
    window.history.state.type &&
    window.history.state.type === 'modal open' &&
    window.history.state.originalPath != modals.path
  ) {
    window.location.href = window.history.state.originalPath;
  }
}

domReady(function() {
  initModals();
});

/**
 * initialize all modal triggers on the page
 */
function initModals() {
  document.documentElement.classList.add(modals.classes.init);

  const modalTriggers = document.querySelectorAll(`.${modals.classes.trigger}`);
  modalTriggers.forEach(function(el) {
    el.addEventListener('click', prepareModal);
  });

  if (modalTriggers) {
    // see if there is a hash that might indicate a modal should already be visible
    if (window.location.hash) {
      checkHashForModal();
    }
  }
}

/**
 * When modal trigger is clicked, perform
 * checks about data location before opening
 * @param {Object} e - mouse event
 */
function prepareModal(e) {
  const el = e.currentTarget;
  const id = el.dataset.micromodalTrigger;
  const modalEl = document.getElementById(id);

  if (modalEl) {
    const link = el.getAttribute('href');

    if (el.dataset.modalBootstrap) {
      const bootstrap = bootstrapData(el.dataset.modalBootstrap, true);

      if (el.dataset.modalTarget) {
        // do push state stuff
        if (link) {
          e.preventDefault();

          if (link != window.location.pathname) {
            history.pushState(
              {
                type: 'modal open',
                originalPath: modals.path,
                href: link,
                target: el.dataset.modalTarget,
              },
              'modal open',
              link
            );
          }
        } else {
          window.location.hash = el.dataset.modalTarget;
        }

        updateModalContent(modalEl, bootstrap[el.dataset.modalTarget]);
        showModal(id, false, el.dataset.modalCb);
      }
    } else if (link) {
      if (link.startsWith('#')) {
        const contentEl = document.getElementById(link.replace('#', ''));

        updateModalContent(modalEl, contentEl.innerHTML);
        showModal(id);
      } else {
        e.preventDefault();

        el.classList.add('is-loading');

        const headers = new Headers();

        if (env.basicauth) {
          headers.append('Authorization', `Basic ${btoa(env.basicauth)}`);
        }

        fetch(link, {headers})
          .then(raiseForStatus)
          .then((res) => res.text())
          .then((html) => {
            const targetSelector = el.dataset.modalTarget
              ? el.dataset.modalTarget
              : 'body';
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const contentEl = doc.querySelector(targetSelector);

            el.classList.remove('is-loading');

            // do push state stuff
            if (link != window.location.pathname) {
              history.pushState(
                {
                  type: 'modal open',
                  originalPath: modals.path,
                  href: link,
                  target: el.dataset.modalTarget,
                },
                'modal open',
                link
              );
            }

            updateModalContent(modalEl, contentEl);
            showModal(id, false, el.dataset.modalCb);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    } else {
      window.location.hash = id;
      showModal(id);
    }
  }
}

/**
 * See if the hash references a modal and
 * show that modal if so.
 */
function checkHashForModal() {
  const id = window.location.hash.replace('#', '');

  // see if the hash is referencing a modal
  const currentModal = document.getElementById(id);
  if (currentModal && currentModal.classList.contains(modals.classes.modal)) {
    showModal(id);
  } else {
    // see if the hash is referenced by a modal trigger
    const currentTrigger =
      document.querySelector(`.${modals.classes.trigger}[href="#${id}"]`) ||
      document.querySelector(
        `.${modals.classes.trigger}[data-modal-target="${id}"]`
      );

    if (currentTrigger) currentTrigger.click();
  }
}

/**
 * Find any open modals and close them.
 */
function closeAllOpenModals() {
  const openModals = document.querySelectorAll(
    `.${modals.classes.modal}.is-open`
  );
  openModals.forEach((modal) => {
    MicroModal.close(modal.id);
  });
}

/**
 * When modal is opened, get item id
 * and add it as window location hash
 * @param {String} id - the html id of the targeted modal
 * @param {Object} extraConfig - an optional object to extend the default modal settings
 * @param {String} callback - callback function to fire
 */
function showModal(id, extraConfig, callback) {
  const config = extraConfig
    ? {...modals.settings, ...extraConfig}
    : modals.settings;

  document.documentElement.classList.add(modals.classes.active);

  MicroModal.show(id, config);

  if (callback) {
    if (typeof callback === 'function') {
      callback({
        id: id,
        modals: modals,
      });
    } else {
      const cbFunction = callback;
      if (window[cbFunction]) {
        window[cbFunction]({
          id: id,
          modals: modals,
        });
      } else {
        // look for the function within objects
        // Split by "." and resolve each segment starting at the window. Invoke with ()
        cbFunction.split('.').reduce((o, n) => o[n], window)({
          id: id,
          modals: modals,
        });
      }
    }
  }
}

/**
 * When modal is closed, remove hash from url
 * @param {Object} obj - the modal object
 */
function closeModal(obj) {
  document.documentElement.classList.remove(modals.classes.active);

  // resets the url when clicking the backdrop is the
  // current url is different from the original page url
  // - due to pushState
  if (window.location.hash || modals.path != window.location.pathname) {
    history.pushState(
      {
        type: 'modal close',
      },
      'modal close',
      modals.path
    );
  }
  // 20190219 from FK to fix Edge Issue
  // Micromodal.js sets the `overflow` value to `initial`
  // when it closes a modal. However, Microsoft Edge 17
  // appears to ignore the assignment and retains the value
  // as 'hidden'. As a workaround to this issue, we reset
  // the style declaration by setting its value to `null`.
  if (document.body.style.overflow === 'hidden') {
    document.body.style.overflow = null;
  }
}

/**
 * Populate modal with content
 * @param {HTMLElement} modalEl - the modal element
 * @param {HTMLElement} content - contents html for the modal
 */
function updateModalContent(modalEl, content) {
  const modalContentEl = modalEl.querySelector(
    `.${modals.classes.innerContent}`
  );
  if (typeof content === 'string') {
    modalContentEl.innerHTML = content;
  } else {
    modalContentEl.innerHTML = '';
    modalContentEl.appendChild(content);
  }
}

/**
 * Monitor the window pop state to help with closing modals
 * @param {Object} event - the event object
 */
window.onpopstate = function(event) {
  if (window.location.hash) {
    if (!document.documentElement.classList.contains(modals.classes.active)) {
      checkHashForModal();
    }
  } else if (
    event.state &&
    event.state.type &&
    event.state.type === 'modal open'
  ) {
    let trigger;
    if (event.state.target) {
      trigger = document.querySelector(
        `.${modals.classes.trigger}[href="${
          event.state.href
        }"][data-modal-target="${event.state.target}"]`
      );
    } else {
      trigger = document.querySelector(
        `.${modals.classes.trigger}[href="${
          event.state.href
        }"]:not([data-modal-target])`
      );
    }

    if (trigger) {
      trigger.click();
    }
  } else {
    closeAllOpenModals();
  }
};

// /* example callback function */
// function testCallback2() {
//   console.log('testing regular callback function');
// }
// /* global */
// window.cbTest = function() {
//   console.log('testing callbacks');
// };
// /* within an object */
// window.foo = {
//   bar: {
//     cbTest: function() {
//       console.log('testing object callbacks');
//     },
//   },
// };
